import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import Modal from '../Modal';
const ALLOWED_TYPES = [
  { value: 'image/jpeg', label: 'JPEG Image' },
  { value: 'image/png', label: 'PNG Image' }, 
  { value: 'image/gif', label: 'GIF Image' },
  { value: 'application/pdf', label: 'PDF Document' },
  { value: 'application/msword', label: 'Word Document' },
  { value: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', label: 'Word Document (DOCX)' },
  { value: 'application/vnd.ms-excel', label: 'Excel Document' },
  { value: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', label: 'Excel Document (XLSX)' }
];
  
const UploadFields = () => {
  const [uploadFields, setUploadFields] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [formData, setFormData] = useState({
    label: '',
    description: '',
    required: false,
    allowedTypes: [],
    maxSize: 5,
    isActive: true
  });
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    fetchUploadFields();
  }, []);

  const fetchUploadFields = async () => {
    try {
      const response = await api.get('/admin/upload-fields');
      setUploadFields(response.data);
    } catch (error) {
      enqueueSnackbar('Error fetching upload fields', { variant: 'error' });
    }
  };

  const handleOpen = (field = null) => {
    if (field) {
      setEditingField(field);
      setFormData({
        label: field.label,
        description: field.description || '',
        required: field.required,
        allowedTypes: field.allowedTypes,
        maxSize: field.maxSize / (1024 * 1024),
        isActive: field.isActive
      });
    } else {
      setEditingField(null);
      setFormData({
        name: '',
        label: '',
        description: '',
        required: false,
        allowedTypes: [],
        maxSize: 5,
        isActive: true
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingField(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        maxSize: formData.maxSize * 1024 * 1024
      };

      if (editingField) {
        await api.put(`/admin/upload-fields/${editingField._id}`, submitData);
        enqueueSnackbar('Upload field updated successfully', { variant: 'success' });
      } else {
        await api.post('/admin/upload-fields', submitData);
        enqueueSnackbar('Upload field created successfully', { variant: 'success' });
      }
      handleClose();
      fetchUploadFields();
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || 'Error saving upload field', { variant: 'error' });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this upload field?')) {
      try {
        await api.delete(`/admin/upload-fields/${id}`);
        enqueueSnackbar('Upload field deleted successfully', { variant: 'success' });
        fetchUploadFields();
      } catch (error) {
        enqueueSnackbar('Error deleting upload field', { variant: 'error' });
      }
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Upload Fields</h1>
        <button
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2"
          onClick={() => handleOpen()}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          Add Upload Field
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white rounded-lg shadow-md">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Field Details</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Allowed Types</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Max Size</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {uploadFields.map((field) => (
              <tr key={field._id}>
                <td className="px-6 py-4">
                  <h2 className="text-sm font-semibold">{field.label}</h2>
                  <p className="text-xs text-gray-600">{field.name}</p>
                  {field.description && (
                    <p className="text-xs text-gray-500 mt-1">{field.description}</p>
                  )}
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-1">
                    {field.allowedTypes.map((type) => (
                      <span key={type} className="px-2 py-0.5 bg-gray-100 rounded-full text-xs">
                        {ALLOWED_TYPES.find(t => t.value === type)?.label || type}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-6 py-4 text-sm">
                  {field.maxSize / (1024 * 1024)}MB
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-col gap-1">
                    <span className={`px-2 py-1 rounded-full text-xs ${field.required ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                      {field.required ? 'Required' : 'Optional'}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs ${field.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {field.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => handleOpen(field)} className="text-gray-600 hover:text-blue-600">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                      </svg>
                    </button>
                    <button onClick={() => handleDelete(field._id)} className="text-gray-600 hover:text-red-600">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <Modal
          isOpen={open}
          onClose={handleClose}
          title={editingField ? 'Edit Upload Field' : 'Add Upload Field'}
          size="lg"
        >
          <form onSubmit={handleSubmit} className="p-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  className="w-full border rounded-md px-3 py-2 bg-gray-100"
                  value={formData.label?.toLowerCase().replace(/\s+/g, '_')}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  readOnly
                  placeholder="Will be generated from label"
                />
                <p className="mt-1 text-sm text-gray-500">Unique identifier for the field</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
                <input
                  type="text"
                  className="w-full border rounded-md px-3 py-2"
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value, name: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                  required
                  placeholder="e.g., Trademark POA"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  className="w-full border rounded-md px-3 py-2"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Allowed File Types</label>
                <select
                  multiple
                  className="w-full border rounded-md px-3 py-2"
                  value={formData.allowedTypes}
                  onChange={(e) => setFormData({ ...formData, allowedTypes: Array.from(e.target.selectedOptions, option => option.value) })}
                >
                  {ALLOWED_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Maximum File Size (MB)</label>
                <input
                  type="number"
                  className="w-full border rounded-md px-3 py-2"
                  value={formData.maxSize}
                  onChange={(e) => setFormData({ ...formData, maxSize: Number(e.target.value) })}
                  min="1"
                  max="10"
                />
              </div>

              <div className="flex gap-6">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-blue-600"
                    checked={formData.required}
                    onChange={(e) => setFormData({ ...formData, required: e.target.checked })}
                  />
                  <span className="text-sm text-gray-700">Required</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-blue-600"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  <span className="text-sm text-gray-700">Active</span>
                </label>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 border rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {editingField ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default UploadFields;